import { Body, Row, Table } from '@table-library/react-table-library/table';
import { useTheme } from '@table-library/react-table-library/theme';
import { CellTree, useTree, TreeExpandClickTypes } from '@table-library/react-table-library/tree';
import { useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useAppSelector } from '@reducers/index';
import { getInitiativeTreeData } from '@selectors/initiativeTree';
import { InitiativePlain } from '@g17eco/types/initiative';
import { UserWithInfo } from '@g17eco/types/workgroup';
import { useGetInitiativeUsersQuery } from '@api/manage-users';
import { UserPermissions } from '@g17eco/types/user';
import { buildTree, StringIdTableNode } from '@features/downloads/util/treeNavigation';
import { findAllChildrenById } from '@utils/initiative';
import { CustomReportSubsidiaryStats } from '../../../../types/custom-report';

interface UserNodesModalProps {
  user: UserWithInfo | null;
  initiativeId: string;
  onClose: () => void;
}

const TABLE_THEME = {
  Table: `
    height: unset;
    max-height: 100%;
    --data-table-library_grid-template-columns: 1fr;

    &.table > :not(caption) > * > * {
      border-bottom-width: 0;
    }
  `,
};

/**
 * Builds tree for user's accessible subsidiaries (direct access + inherited descendants).
 */
const buildAccessibleTree = (
  userPermissions: UserPermissions[],
  initiatives: InitiativePlain[]
): StringIdTableNode[] => {
  const directIds = new Set(userPermissions.map((p) => p.initiativeId));

  // Root nodes = direct access without accessible parent
  const rootIds = [...directIds].filter((id) => {
    const init = initiatives.find((i) => i._id === id);
    return init && (!init.parentId || !directIds.has(init.parentId));
  });

  // Collect roots + all descendants
  const accessibleList = rootIds.flatMap((rootId) => {
    const root = initiatives.find((i) => i._id === rootId);
    return root ? [root, ...findAllChildrenById(initiatives, rootId)] : [];
  });

  // Convert to format required by buildTree
  const list: CustomReportSubsidiaryStats[] = accessibleList.map((i) => ({
    _id: i._id,
    name: i.name,
    parentId: i.parentId,
    surveyCount: 0,
  }));

  return rootIds.flatMap((id) => buildTree(list, id).nodes);
};

export const UserNodesModal = ({ user, initiativeId, onClose }: UserNodesModalProps) => {
  const fullInitiativeTree = useAppSelector(getInitiativeTreeData);
  const { data: initiativeUsersData } = useGetInitiativeUsersQuery(
    { initiativeId, includeSubsidiaries: true },
    { skip: !user }
  );

  const nodes = useMemo(() => {
    const userData = initiativeUsersData?.users?.find((u) => u._id === user?._id);
    if (!userData?.permissions?.length || !fullInitiativeTree?.length) {
      return [];
    }
    return buildAccessibleTree(userData.permissions, fullInitiativeTree);
  }, [initiativeUsersData, user, fullInitiativeTree]);

  const tree = useTree(
    { nodes },
    {},
    {
      treeIcon: {
        margin: '4px',
        iconDefault: <i className='fa' style={{ marginLeft: '8px', marginRight: '12px' }} />,
        iconRight: <i className='fa fa-caret-right text-ThemeIconSecondary' style={{ margin: '0 8px' }} />,
        iconDown: <i className='fa fa-caret-down text-ThemeIconSecondary' style={{ margin: '0 8px' }} />,
      },
      clickType: TreeExpandClickTypes.RowClick,
    }
  );

  const theme = useTheme(TABLE_THEME);

  if (!user) {
    return null;
  }

  return (
    <Modal isOpen toggle={onClose} size='lg' centered backdrop='static'>
      <ModalHeader toggle={onClose}>
        {user.name} Subsidiary Access
      </ModalHeader>
      <ModalBody>
        {nodes.length > 0 ? (
          <div className='text-md' style={{ maxHeight: '400px', overflow: 'auto' }}>
            <Table data={{ nodes }} tree={tree} theme={theme} layout={{ fixedHeader: true, custom: true }}>
              {(tableList: StringIdTableNode[]) => (
                <Body>
                  {tableList.map((item) => (
                    <Row key={item.id} item={item}>
                      <CellTree item={item}>{item.name}</CellTree>
                    </Row>
                  ))}
                </Body>
              )}
            </Table>
          </div>
        ) : (
          <p>No accessible nodes</p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color='secondary' onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
